import { useState, useRef, useCallback, useEffect } from "react";
import "./AngleSlider.css";
import angleButtonImage from "../assets/angle_button.png";

interface AngleSliderProps {
  pitchPosition: number;
  onChange: (value: number) => void;
}

const PITCH_POSITIONS = [-2, -1, 0, 1, 2];
const POSITION_PERCENTAGES: Record<number, number> = {
  [-2]: 18,
  [-1]: 34,
  [0]: 50,
  [1]: 66,
  [2]: 82,
};

export function AngleSlider({ pitchPosition, onChange }: AngleSliderProps) {
  const [position, setPosition] = useState(
    POSITION_PERCENTAGES[pitchPosition] ?? 50,
  );
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const lastSentPitch = useRef(pitchPosition);
  const activeTouchId = useRef<number | null>(null);

  useEffect(() => {
    const newPosition = POSITION_PERCENTAGES[pitchPosition] ?? 50;
    setPosition(newPosition);
    lastSentPitch.current = pitchPosition;
  }, [pitchPosition]);

  const calculateValue = useCallback(
    (clientY: number) => {
      if (!trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const relativeY = clientY - rect.top;
      const percentage = (relativeY / rect.height) * 100;

      let closestPitch = 0;
      let minDistance = Infinity;

      for (const pitch of PITCH_POSITIONS) {
        const pitchPercent = POSITION_PERCENTAGES[pitch] ?? 50;
        const distance = Math.abs(percentage - pitchPercent);
        if (distance < minDistance) {
          minDistance = distance;
          closestPitch = pitch;
        }
      }

      const newPosition = POSITION_PERCENTAGES[closestPitch] ?? 50;
      setPosition(newPosition);

      if (closestPitch !== lastSentPitch.current) {
        lastSentPitch.current = closestPitch;
        onChange(closestPitch);
      }
    },
    [onChange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      calculateValue(e.clientY);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (isDragging.current) {
          calculateValue(moveEvent.clientY);
        }
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [calculateValue],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;

      isDragging.current = true;
      activeTouchId.current = touch.identifier;
      calculateValue(touch.clientY);

      const handleTouchMove = (moveEvent: TouchEvent) => {
        if (!isDragging.current || activeTouchId.current === null) return;

        const moveTouch = Array.from(moveEvent.touches).find(
          (t) => t.identifier === activeTouchId.current,
        );

        if (moveTouch) {
          moveEvent.preventDefault();
          calculateValue(moveTouch.clientY);
        }
      };

      const handleTouchEnd = (endEvent: TouchEvent) => {
        const endedTouch = Array.from(endEvent.changedTouches).find(
          (t) => t.identifier === activeTouchId.current,
        );

        if (endedTouch) {
          isDragging.current = false;
          activeTouchId.current = null;
          document.removeEventListener("touchmove", handleTouchMove);
          document.removeEventListener("touchend", handleTouchEnd);
        }
      };

      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    },
    [calculateValue],
  );

  return (
    <div className="angle-slider">
      <div
        className="angle-slider__track"
        ref={trackRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div
          className="angle-slider__button"
          style={{ top: `${position}%` }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <img src={angleButtonImage} alt="Angle control" draggable={false} />
        </div>
      </div>
    </div>
  );
}
